import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@/hooks/ui/useDebouncedValue';
import { UserService } from '@/services/users/UserService';
import { Profile } from '@/types/auth/auth';
import { useRole } from './useRole';

export const useProfiles = (excludeRole?: string) => {
  const { isInternalAdmin } = useRole();

  return useQuery<Profile[]>({
    queryKey: ["auth", "profiles", excludeRole, isInternalAdmin()],
    queryFn: () => (isInternalAdmin() ? UserService.getProfiles(excludeRole) : Promise.resolve([])),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });
};

export const PROFILE_SEARCH_MIN_LENGTH = 3;

/** Admin-only name/email search; waits for a typing pause and at least three characters. */
export const useProfileSearch = (searchTerm: string) => {
  const { isInternalAdmin } = useRole();
  const term = useDebouncedValue(searchTerm.trim(), 400);
  const enabled = isInternalAdmin() && term.length >= PROFILE_SEARCH_MIN_LENGTH;

  const query = useQuery<Profile[]>({
    queryKey: ["auth", "profiles", "search", term],
    queryFn: () => UserService.searchProfiles(term),
    enabled,
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });

  return { ...query, enabled, isDebouncing: term !== searchTerm.trim() };
};
